import React from 'react';
import {getFirstCapitals} from '../../../../utils/getFirstCapitals';
import {courseInstructorText} from '../courseInstructorText';
import Button from '../../../../uiToolkit/Buttons/Button';
import {CourseInstructor} from '../../../../data/models/Course/Course';
import LearnersTable, {
  LearnersTableRow,
} from '../../../../modules/LearnersTable/LearnersTable';
import LearnersEmpty from '../../../../modules/LearnersTable/LearnersEmpty';
import LearnersSearch from '../../../../modules/LearnersTable/LearnersSearch';
import CourseNotPublished from '../../components/CourseNotPublished';
import './styles.css';

interface Props {
  instructorData: any[];
  onSave: (selectedInstructors: string) => void;
  currentInstructor?: CourseInstructor;
  isLoading: boolean;
  publishedCourse?: any;
  onRemove: () => void;
}

const PAGE_SIZE = 10;

const CourseIntructorUI = React.memo(
  ({
    onSave,
    instructorData,
    currentInstructor,
    isLoading,
    publishedCourse,
    onRemove,
  }: Props) => {
    const [checkedInstructorId, setCheckedInstructorId] = React.useState('');
    const [search, setSearch] = React.useState('');
    const [page, setPage] = React.useState(1);

    const candidates = instructorData.filter(
      candidate => candidate.staffMemberId !== currentInstructor?.instructorId,
    );
    const term = search.trim().toLowerCase();
    const filtered = term
      ? candidates.filter(
          candidate =>
            (candidate.name || '').toLowerCase().includes(term) ||
            (candidate.email || '').toLowerCase().includes(term),
        )
      : candidates;
    const pages = Math.ceil(filtered.length / PAGE_SIZE);
    const rows: LearnersTableRow[] = filtered
      .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      .map(candidate => ({
        learnerId: candidate.staffMemberId,
        name: candidate.name,
        email: candidate.email,
        avatar: candidate.avatarUrl,
        roleName: candidate.roleName,
      }));

    const onSearchChange = (value: string) => {
      setSearch(value);
      setPage(1);
      setCheckedInstructorId('');
    };

    const onToggle = (instructorId: string) => {
      setCheckedInstructorId(current =>
        current === instructorId ? '' : instructorId,
      );
    };

    const onAssign = () => {
      onSave(checkedInstructorId);
      setCheckedInstructorId('');
    };

    const renderCurrentCard = () => {
      if (!currentInstructor) {
        return (
          <div className="InstructorCard InstructorCardEmpty">
            <span className="InstructorCardAvatar InstructorCardAvatarEmpty">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#7d8b94"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </span>
            <div className="InstructorCardInfo">
              <span className="InstructorCardEyebrow">
                {courseInstructorText.currentInstructor}
              </span>
              <p className="InstructorCardTitle">
                {courseInstructorText.noInstructorTitle}
              </p>
              <p className="InstructorCardText">
                {courseInstructorText.noInstructorSubtitle}
              </p>
            </div>
          </div>
        );
      }
      return (
        <div className="InstructorCard">
          <span className="InstructorCardAvatar">
            {getFirstCapitals(currentInstructor.instructor.userName)}
          </span>
          <div className="InstructorCardInfo">
            <span className="InstructorCardEyebrow">
              {courseInstructorText.currentInstructor}
            </span>
            <p className="InstructorCardTitle">
              {currentInstructor.instructor.userName}
            </p>
            <p className="InstructorCardText">
              {currentInstructor.instructor.email}
            </p>
          </div>
          <button
            type="button"
            className="InstructorRemoveButton"
            onClick={onRemove}>
            {courseInstructorText.removeInstructor}
          </button>
        </div>
      );
    };

    return (
      <div className="CourseEditContainer">
        {publishedCourse ? (
          <div className="AddInstructorWizardContainer">
            {renderCurrentCard()}
            <div className="InstructorHeadingRow">
              <div>
                <h3 className="InstructorHeading">
                  {currentInstructor
                    ? courseInstructorText.replaceHeading
                    : courseInstructorText.assignHeading}
                </h3>
                <p className="InstructorHint">
                  {courseInstructorText.oneInstructorHint}
                </p>
              </div>
              <div className="InstructorSearch">
                <LearnersSearch
                  value={search}
                  onChange={onSearchChange}
                  placeholder={courseInstructorText.searchInstructors}
                />
              </div>
            </div>
            {rows.length ? (
              <LearnersTable
                learners={rows}
                checked={checkedInstructorId ? [checkedInstructorId] : []}
                page={page}
                pages={pages}
                onToggle={onToggle}
                onPageChange={setPage}
                single
                showRole
                footerLabel={courseInstructorText.instructorsCount(
                  filtered.length,
                )}
              />
            ) : (
              <LearnersEmpty title={courseInstructorText.noResults} />
            )}
            <div className="InstructorFooter">
              <Button
                title={
                  currentInstructor
                    ? courseInstructorText.replaceInstructor
                    : courseInstructorText.assignInstructor
                }
                onClick={onAssign}
                uiType="action"
                disabled={!checkedInstructorId}
                isLoading={isLoading}
                className="SaveButton"
              />
              {!checkedInstructorId && (
                <span className="InstructorFooterHint">
                  {courseInstructorText.selectToAssign}
                </span>
              )}
            </div>
          </div>
        ) : (
          <CourseNotPublished subtitle={courseInstructorText.notPublished} />
        )}
      </div>
    );
  },
);

export default CourseIntructorUI;
